import { importSigningKey } from '../index.js'

/** A partner's signing key, loaded from its key file for this page only. */
export interface LoadedPartnerKey {
  kid: string
  signingKey: CryptoKey
}

/** The key file could not be used - the message is written for the person who chose the file. */
export class PartnerKeyFileError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PartnerKeyFileError'
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Reads a key file as downloaded at onboarding (`{ kid, publicJwk, privateJwk }`,
 * the same shape the onboarding CLI stores). The private key is imported as a
 * non-extractable signing key and the file's text is not kept, so nothing on
 * the page can read the key material back out.
 */
export async function loadPartnerKeyFile(text: string): Promise<LoadedPartnerKey> {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    throw new PartnerKeyFileError('This file is not JSON. Choose the key file downloaded when the client was onboarded.')
  }

  if (!isRecord(parsed) || typeof parsed['kid'] !== 'string' || parsed['kid'] === '' || !isRecord(parsed['privateJwk'])) {
    throw new PartnerKeyFileError('This file is not a partner key file: it needs a "kid" and a "privateJwk".')
  }

  const privateJwk = parsed['privateJwk']
  if (privateJwk['kty'] !== 'EC' || privateJwk['crv'] !== 'P-256' || typeof privateJwk['d'] !== 'string') {
    throw new PartnerKeyFileError('The private key in this file is not an ES256 (P-256) private key.')
  }

  try {
    return { kid: parsed['kid'], signingKey: await importSigningKey(privateJwk as JsonWebKey) }
  } catch {
    throw new PartnerKeyFileError('This browser could not load the private key in this file.')
  }
}
